import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import feedbackApis from '~/apis/feedback';
import { AnimatedSection, SEO } from '~/components/common';
import { signUpURL } from '~/utils/constants/env';

type FeedbackType = 'bug' | 'feature' | 'general';

const About: React.FC = () => {
  //#region Declare Hook
  const { t } = useTranslation();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('general');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || sending) return;
    setSending(true);
    setSubmitError(false);
    try {
      await feedbackApis.submit({ type: feedbackType, message: message.trim(), email: email.trim() || undefined });
      setSubmitted(true);
      setMessage('');
      setEmail('');
    } catch {
      setSubmitError(true);
    } finally {
      setSending(false);
    }
  };
  //#endregion Handle Function

  const values = [
    { id: 'personal', icon: '🎯', gradient: 'from-lumi-500 to-lumi-cyan-500' },
    { id: 'simple', icon: '✨', gradient: 'from-purple-500 to-pink-500' },
    { id: 'intelligent', icon: '🧠', gradient: 'from-blue-500 to-indigo-500' },
    { id: 'consistent', icon: '🔥', gradient: 'from-amber-500 to-orange-500' },
  ];

  return (
    <div className='min-h-screen bg-lumi-dark-300 text-white'>
      <SEO type='about' />
      {/* Hero */}
      <section className='pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden'>
        <div className='absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-lumi-700/15 blur-[100px] pointer-events-none' />
        <AnimatedSection animation='slide-up' className='relative z-10 max-w-3xl mx-auto'>
          <h1 className='text-4xl sm:text-5xl font-bold text-white mb-6'>{t('about.hero.title')}</h1>
          <p className='text-white/60 text-lg leading-relaxed'>{t('about.hero.subtitle')}</p>
        </AnimatedSection>
      </section>

      {/* Story */}
      <section className='py-20 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-4xl mx-auto'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-16 items-center'>
            <AnimatedSection animation='slide-right'>
              <h2 className='text-3xl font-bold text-white mb-8'>{t('about.story.title')}</h2>
              <div className='space-y-5'>
                <p className='text-white/60 leading-relaxed'>{t('about.story.p1')}</p>
                <p className='text-white/60 leading-relaxed'>{t('about.story.p2')}</p>
                <p className='text-white/60 leading-relaxed'>{t('about.story.p3')}</p>
              </div>
            </AnimatedSection>
            <AnimatedSection animation='slide-left' delay={150} className='flex items-center justify-center'>
              <div className='w-full max-w-sm h-80 rounded-3xl bg-gradient-to-br from-lumi-800/50 to-lumi-dark-200 border border-lumi-500/30 flex items-center justify-center relative overflow-hidden'>
                <div className='absolute top-8 left-8 w-16 h-16 rounded-2xl overflow-hidden'>
                  <img src={require('~/assets/images/logo.svg').default} alt='Lumi' className='w-full h-full' />
                </div>
                <div className='text-center'>
                  <div className='text-5xl mb-4'>🌱</div>
                  <p className='text-white/60 text-sm font-medium'>{t('about.story.visual')}</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className='py-20 px-4 sm:px-6 lg:px-8 bg-white/[0.02]'>
        <div className='max-w-3xl mx-auto text-center'>
          <AnimatedSection animation='scale-in' className='p-10 rounded-3xl bg-gradient-to-br from-lumi-800/40 to-transparent border border-lumi-500/30 relative overflow-hidden'>
            <div className='absolute inset-0 bg-gradient-to-br from-lumi-600/10 to-transparent pointer-events-none' />
            <div className='relative z-10'>
              <div className='text-4xl mb-6'>🚀</div>
              <h2 className='text-2xl font-bold text-white mb-4'>{t('about.mission.title')}</h2>
              <p className='text-white/60 text-lg leading-relaxed italic'>&ldquo;{t('about.mission.description')}&rdquo;</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Values */}
      <section className='py-20 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-6xl mx-auto'>
          <AnimatedSection animation='slide-up' className='mb-12 text-center'>
            <h2 className='text-3xl font-bold text-white'>{t('about.values.title')}</h2>
          </AnimatedSection>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
            {values.map((value, index) => (
              <AnimatedSection
                key={value.id}
                animation='slide-up'
                delay={index * 100}
                className='p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-lumi-500/40 transition-all duration-300 hover:-translate-y-1 text-center'
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${value.gradient} flex items-center justify-center text-2xl mb-5 mx-auto`}>
                  {value.icon}
                </div>
                <h3 className='text-white font-semibold text-lg mb-3'>{t(`about.values.${value.id}.title`)}</h3>
                <p className='text-white/50 text-sm leading-relaxed'>{t(`about.values.${value.id}.description`)}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Feedback */}
      <section className='py-20 px-4 sm:px-6 lg:px-8 bg-white/[0.02]'>
        <div className='max-w-2xl mx-auto'>
          <AnimatedSection animation='slide-up' className='text-center mb-10'>
            <h2 className='text-3xl font-bold text-white mb-4'>{t('about.feedback.title')}</h2>
            <p className='text-white/60 leading-relaxed'>{t('about.feedback.subtitle')}</p>
          </AnimatedSection>
          <AnimatedSection animation='scale-in' delay={100}>
            {submitted ? (
              <div className='text-center py-12 px-8 rounded-3xl bg-white/5 border border-lumi-500/30'>
                <div className='text-5xl mb-4'>🎉</div>
                <p className='text-lumi-400 font-semibold text-lg'>{t('about.feedback.success')}</p>
              </div>
            ) : (
              <form onSubmit={handleFeedbackSubmit} className='p-8 rounded-3xl bg-white/5 border border-white/10 space-y-6'>
                {/* Type selector */}
                <div className='flex flex-wrap gap-3'>
                  {(['bug', 'feature', 'general'] as FeedbackType[]).map((type) => (
                    <button
                      key={type}
                      type='button'
                      onClick={() => setFeedbackType(type)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                        feedbackType === type
                          ? 'bg-lumi-600 text-white border border-lumi-500'
                          : 'bg-white/5 text-white/60 border border-white/10 hover:border-lumi-500/40 hover:text-white'
                      }`}
                    >
                      {t(`about.feedback.type${type.charAt(0).toUpperCase() + type.slice(1)}`)}
                    </button>
                  ))}
                </div>
                {/* Message */}
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('about.feedback.messagePlaceholder')}
                  rows={5}
                  required
                  minLength={10}
                  maxLength={2000}
                  className='w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-lumi-500/60 resize-none transition-colors duration-200'
                />
                {/* Optional email */}
                <input
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('about.feedback.emailPlaceholder')}
                  className='w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-lumi-500/60 transition-colors duration-200'
                />
                {submitError && (
                  <p className='text-red-400 text-sm'>{t('about.feedback.error')}</p>
                )}
                <button
                  type='submit'
                  disabled={sending || message.trim().length < 10}
                  className='w-full py-3 rounded-xl bg-lumi-600 hover:bg-lumi-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-all duration-200 hover:shadow-lg hover:shadow-lumi-600/30'
                >
                  {sending ? t('about.feedback.sending') : t('about.feedback.submit')}
                </button>
              </form>
            )}
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className='py-24 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-3xl mx-auto text-center'>
          <AnimatedSection animation='slide-up'>
            <h2 className='text-3xl font-bold text-white mb-8'>{t('about.cta.title')}</h2>
            <a
              href={signUpURL}
              className='inline-flex items-center px-8 py-4 rounded-xl bg-lumi-600 hover:bg-lumi-500 text-white font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-lumi-600/40 hover:-translate-y-0.5'
            >
              {t('about.cta.button')}
            </a>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default About;
